import { addDays, dailyKey, type DateKey, daysBetween, levelName, practiceKey } from '@arrow/game-engine';
import { DEFAULT_LEVEL, type Level, LEVELS, type Puzzle } from '@arrow/puzzle-schema';
import { type CSSProperties, type ReactNode, useEffect, useMemo, useRef, useState } from 'react';

import {
	ApiError,
	type DailyLevelSummary,
	type DailyPuzzle,
	fetchDailyByNumber,
	fetchDailyLevels,
	fetchPracticePuzzle,
	type LoadError,
} from '../api/client.ts';
import { cacheDaily, cachedDaily, cachedLevels, cacheLevels, clearHandoff, type Handoff, peekHandoff } from '../app/handoff.ts';
import { setPageTitle } from '../app/page-title.ts';
import type { Prefs } from '../app/prefs.ts';
import { backToPlay, dailyPath, type PlayContext } from '../app/play-context.ts';
import { goUp, navigate } from '../app/router.tsx';
import { useGameLayout } from '../app/useMedia.ts';
import { dailyNumberOf } from '../app/schedule.ts';
import { AppMenu } from '../components/AppMenu.tsx';
import { BackPill, type BackTo } from '../components/BackPill.tsx';
import { Game } from '../components/Game.tsx';
import { Wedge } from '../components/icons.tsx';
import { ErrorState, Pending, Skeleton, StateBody, StatePage } from '../components/States.tsx';
import type { DevControls } from '../dev/types.ts';
import { devModeOn } from '../dev/mode.ts';
import { KEY_ROWS } from '../game/keyboard.ts';
import { noteLeftDaily } from '../game/recent.ts';
import type { GameSession } from '../game/useGame.ts';
import { dayMonth, weekdayKey, weekdayName } from '../game/view.ts';
import { track } from '../platform/analytics.ts';
import { playingChallenge } from '../platform/challenges.ts';
import { takeJoined } from '../nests/store.ts';
import { type Persistence, slotFor } from '../storage/persistence.ts';
import { usePlayNext } from './play/next.ts';

/** In dev mode, a puzzle id can also be a bundled fixture (puzzles/fixtures) or a dev puzzle (lazily loaded). */
const loadDevPuzzle = (id: string, signal: AbortSignal): Promise<Puzzle> => import('../dev/api.ts').then((m) => m.loadDevPuzzle(id, signal));

type Ready = {
	status: 'ready';
	puzzle: Puzzle;
	session: GameSession;
	/** An older daily: plays from the archive, never for the streak. */
	archive: boolean;
	daily: DailyPuzzle | null;
};
/**
 * `missing`: a link to a Sparrow that doesn't exist and never will (UX-022). `later`: one
 * that isn't out yet, with the day it comes out (QA2-7).
 */
type Load =
	| { status: 'loading' }
	| { status: 'error'; error: LoadError }
	| { status: 'missing' }
	| { status: 'later'; date: DateKey; today: number }
	| Ready;

function toError(err: unknown): LoadError {
	return err instanceof ApiError ? err.detail : { kind: 'network', message: 'Something went wrong loading the puzzle.' };
}

/**
 * A daily the api hasn't got: one that isn't out yet gets the day it comes out (numbers
 * run one a day from today's); a number below 1, or more than two days on, will never
 * exist. Today's number comes from the stored schedule, else the day's levels (one small
 * fetch). Null when it can't tell: then it's the ordinary "not found" error.
 */
export async function dailyAbsent(
	n: number,
	todayKey: DateKey,
	signal: AbortSignal,
): Promise<{ status: 'missing' } | { status: 'later'; date: DateKey; today: number } | null> {
	if (n < 1) return { status: 'missing' };
	let today = dailyNumberOf(todayKey);
	if (today === null) today = await fetchDailyLevels(todayKey, signal).then((l) => l.number, () => null);
	if (today === null) return null;
	if (n > today + 2) return { status: 'missing' };
	if (n > today) return { status: 'later', date: addDays(todayKey, n - today), today };
	return null;
}

function dailyReady(daily: DailyPuzzle, todayKey: DateKey): Ready {
	const slot = slotFor({ kind: 'daily', date: daily.date, level: daily.level });
	// Opened from a challenge link in this tab: race the challenger.
	const challenge = playingChallenge(slot);
	return {
		status: 'ready',
		puzzle: daily.puzzle,
		daily,
		archive: daily.date < todayKey,
		session: {
			kind: 'daily',
			slot,
			puzzleId: daily.puzzle.id,
			dateKey: daily.date,
			number: daily.number,
			level: daily.level,
			...(challenge && challenge.number === daily.number && challenge.level === daily.level ? { challenge } : {}),
		},
	};
}

/**
 * A daily as it opens: ready to play, or, when the api has already released a later day
 * (it serves up to UTC today + 1, so a friend ahead of us can send tomorrow's), not out here
 * yet (J10). It opens on its own day, so it never plays early and never touches a streak.
 */
export function readyOrLater(daily: DailyPuzzle, todayKey: DateKey): Load {
	if (daily.date > todayKey) return { status: 'later', date: daily.date, today: daily.number - daysBetween(todayKey, daily.date) };
	return dailyReady(daily, todayKey);
}

/** "You’re in Family": the clue band's slip is one line, so a long name is shortened. */
export function joinedLine(name: string, room = 24): string {
	const n = name.trim();
	return `You’re in ${n.length > room ? `${n.slice(0, room - 1).trimEnd()}…` : n}`;
}

/** The other levels, nearest to `level` first (the easier on a tie). */
export function nearestLevels(level: Level): Level[] {
	const at = LEVELS.indexOf(level);
	return LEVELS.filter((l) => l !== level).sort((a, b) => Math.abs(LEVELS.indexOf(a) - at) - Math.abs(LEVELS.indexOf(b) - at) || LEVELS.indexOf(a) - LEVELS.indexOf(b));
}

const notFound = (err: unknown) => err instanceof ApiError && err.detail.kind === 'not-found';

/**
 * Sparrow No. n at a level. A day without that level opens the nearest level it has
 * (QA2-7): asked through the day's level list where it can be, so a day from before
 * levels is never a 404. Throws the asked level's error when the day has no puzzle at all.
 */
async function loadDaily(n: number, level: Level, signal: AbortSignal): Promise<DailyPuzzle> {
	const get = async (l: Level) => {
		const d = cachedDaily({ number: n }, l) ?? (await fetchDailyByNumber(n, l, signal));
		cacheDaily(d);
		return d;
	};
	const hit = cachedDaily({ number: n }, level);
	if (hit) return hit;
	// Medium first: every day has had one, and its date gives the day's levels.
	let medium: DailyPuzzle | null = null;
	let firstError: unknown = null;
	try {
		medium = await get(DEFAULT_LEVEL);
	} catch (err) {
		if (!notFound(err)) throw err;
		firstError = err;
	}
	if (medium) {
		if (level === DEFAULT_LEVEL) return medium;
		const levels = cachedLevels(medium.date) ?? (await fetchDailyLevels(medium.date, signal));
		cacheLevels(levels);
		const has = levels.levels.map((l) => l.level);
		if (has.includes(level)) return get(level);
		const near = nearestLevels(level).find((l) => has.includes(l)) ?? DEFAULT_LEVEL;
		return near === DEFAULT_LEVEL ? medium : get(near);
	}
	// No medium: the day's other levels, nearest first.
	for (const l of [level, ...nearestLevels(level)].filter((x) => x !== DEFAULT_LEVEL)) {
		try {
			return await get(l);
		} catch (err) {
			if (!notFound(err)) throw err;
		}
	}
	throw firstError;
}

interface DailyScreenProps {
	number: number;
	/** `?l=` on the route, if any. */
	level: Level | null;
	todayKey: DateKey;
	persistence: Persistence;
	prefs: Prefs;
	/** How this open arrived (for `daily_opened`), when it isn't from Today's hand-over. */
	via: 'link' | 'share';
	/** A first visit from a share link: say hello over the masthead. */
	firstShareVisit: boolean;
	/** Opened from Play: "‹ Play" goes back to that stack or search, and the poster offers the next in it. */
	ctx?: PlayContext | null;
	/** This device's first day (for what Play counts as missed). */
	firstSeen?: DateKey | null;
	dev?: DevControls | undefined;
}

/** `/daily/:n`: today's number plays as the daily; an older one as an archive day. */
export function DailyScreen({ number, level: asked, todayKey, persistence, prefs, via, firstShareVisit, ctx = null, firstSeen = null, dev }: DailyScreenProps) {
	const back = ctx ? backToPlay(ctx) : '/';
	const level = asked ?? prefs.lastLevel() ?? DEFAULT_LEVEL;
	const [attempt, setAttempt] = useState(0);
	// The link asked for a level that day doesn't have: it opened the nearest, and says so.
	const [swapped, setSwapped] = useState<{ asked: Level; got: Level } | null>(null);
	// Just joined a nest from its invite (QA2-13, F2's `noteJoined`): say so on the clue band.
	const [joined, setJoined] = useState<string | null>(null);
	useEffect(() => {
		const name = takeJoined();
		if (name) setJoined(joinedLine(name));
	}, []);
	const [load, setLoad] = useState<Load>(() => {
		const hit = cachedDaily({ number }, level);
		return hit ? readyOrLater(hit, todayKey) : { status: 'loading' };
	});

	useEffect(() => {
		const ctrl = new AbortController();
		const hit = cachedDaily({ number }, level);
		if (hit) {
			setLoad(readyOrLater(hit, todayKey));
			return;
		}
		setLoad({ status: 'loading' });
		loadDaily(number, level, ctrl.signal).then(
			(d) => {
				if (d.level !== level) {
					if (asked) setSwapped({ asked, got: d.level });
					navigate(dailyPath(number, d.level, ctx), { replace: true });
				}
				setLoad(readyOrLater(d, todayKey));
			},
			async (err: unknown) => {
				if (ctrl.signal.aborted) return;
				const error = toError(err);
				const absent = error.kind === 'not-found' ? await dailyAbsent(number, todayKey, ctrl.signal) : null;
				if (!ctrl.signal.aborted) setLoad(absent ?? { status: 'error', error });
			},
		);
		return () => ctrl.abort();
	}, [number, level, todayKey, attempt]);

	const ready = load.status === 'ready' ? load : null;
	const slot = ready?.session.slot ?? null;
	// The hand-over from Today (used once per open; a deep link has none).
	const handoff = useMemo<Handoff | null>(() => (slot ? peekHandoff(slot) : null), [slot]);
	useEffect(() => clearHandoff(handoff), [handoff]);

	// Today's daily: the day's levels, so the poster can say what's left today.
	const date = ready && ready.session.dateKey === todayKey ? ready.session.dateKey : null;
	const [dayLevels, setDayLevels] = useState<readonly DailyLevelSummary[] | null>(null);
	useEffect(() => {
		if (!date) return;
		const hit = cachedLevels(date);
		if (hit) return setDayLevels(hit.levels);
		const ctrl = new AbortController();
		fetchDailyLevels(date, ctrl.signal).then(
			(l) => {
				cacheLevels(l);
				setDayLevels(l.levels);
			},
			() => {},
		);
		return () => ctrl.abort();
	}, [date]);

	// Opened: track how it was reached, and title the tab (never an answer). The level isn't
	// remembered on open, only on a solve of today's (QA2-8), so a look at an old Hard doesn't
	// change Today.
	const tracked = useRef<string | null>(null);
	useEffect(() => {
		if (!ready || tracked.current === ready.session.slot) return;
		tracked.current = ready.session.slot;
		const { session } = ready;
		setPageTitle(`No. ${session.number}${session.level ? `, ${levelName(session.level)}` : ''}`);
		const dateKey = session.dateKey ?? todayKey;
		track('daily_opened', {
			number: session.number ?? number,
			level: session.level ?? DEFAULT_LEVEL,
			isToday: session.dateKey === todayKey,
			via: session.challenge ? 'challenge' : handoff ? 'today' : (ctx?.from ?? via),
			daysAgo: Math.max(0, daysBetween(dateKey, todayKey)),
		});
		// Once per game opened.
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [slot]);
	// Leaving a daily: Today knows you've just come back from it (QA2-9).
	const left = ready?.session.kind === 'daily' && ready.session.dateKey && ready.session.level ? { date: ready.session.dateKey, level: ready.session.level } : null;
	useEffect(() => {
		if (!left) return;
		return () => noteLeftDaily(left.date, left.level);
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [left?.date, left?.level]);

	const playNext = usePlayNext(ctx, todayKey, firstSeen, persistence, ready?.session.dateKey && ready.session.level ? dailyKey(ready.session.dateKey, ready.session.level) : null);

	if (load.status === 'loading') {
		// The day it will be, for the masthead's weight (numbers run one a day).
		const today = dailyNumberOf(todayKey);
		const day = today === null ? todayKey : addDays(todayKey, number - today);
		return <GameLoading label={`Loading Sparrow No. ${number}`} back={back} level={level} dateKey={day} />;
	}
	if (load.status === 'missing') return <Missing back={back} fromPlay={!!ctx} />;
	if (load.status === 'later') return <NotOut number={number} date={load.date} today={load.today} tomorrow={load.date === addDays(todayKey, 1)} back={back} />;
	if (load.status === 'error')
		return (
			<ErrorState
				error={load.error}
				what={`Sparrow No. ${number}`}
				onRetry={() => setAttempt((n) => n + 1)}
				link={{ label: ctx ? 'Back to Play' : 'Back to Today', onClick: () => goUp(back) }}
				back={back}
				dev={dev ? { message: true } : undefined}
				game
				screen="game"
			/>
		);

	const { session, puzzle, archive } = load;
	const isToday = session.dateKey === todayKey;
	const welcome: ReactNode = swapped ? (
		<>
			<Wedge direction="diagonal" className="nm" />
			<span>
				No. {number} has no {levelName(swapped.asked)}.<span className="q"> Here’s its {levelName(swapped.got)}.</span>
			</span>
		</>
	) : firstShareVisit ? (
		<>
			<Wedge direction="diagonal" className="nm" />
			<span>
				Welcome to Sparrow
				<span className="q">{isToday ? ` · Today’s Arrowword, No. ${session.number}` : ` · No. ${session.number}, from the archive`}</span>
			</span>
		</>
	) : null;

	return (
		<Game
			key={session.slot}
			puzzle={puzzle}
			session={session}
			todayKey={todayKey}
			persistence={persistence}
			dev={dev}
			back={back}
			dayLevels={dayLevels}
			intro={handoff}
			archive={archive}
			welcome={welcome}
			playNext={playNext}
			// Today remembers your level from a solve of today's daily, not from opening one.
			onSolved={isToday && session.level ? () => prefs.setLastLevel(session.level!) : undefined}
			notice={joined}
		/>
	);
}

interface PracticeScreenProps {
	id: string;
	todayKey: DateKey;
	persistence: Persistence;
	/** Opened from Play's Practice stack: back to it, and the next in it. */
	ctx?: PlayContext | null;
	firstSeen?: DateKey | null;
	dev?: DevControls | undefined;
}

/** `/play/:id`: a practice puzzle (no streak). Dev builds also open fixtures and dev puzzles by id. */
export function PracticeScreen({ id, todayKey, persistence, ctx = null, firstSeen = null, dev }: PracticeScreenProps) {
	const back = backToPlay(ctx);
	const playNext = usePlayNext(ctx, todayKey, firstSeen, persistence, practiceKey(id));
	const [attempt, setAttempt] = useState(0);
	const [load, setLoad] = useState<Load>({ status: 'loading' });

	useEffect(() => {
		const ctrl = new AbortController();
		setLoad({ status: 'loading' });
		const practice = fetchPracticePuzzle(id, ctrl.signal).then(
			(p): Ready => ({
				status: 'ready',
				puzzle: p.puzzle,
				daily: null,
				archive: false,
				session: {
					kind: 'practice',
					slot: slotFor({ kind: 'practice', puzzleId: p.id }),
					puzzleId: p.id,
					dateKey: null,
					number: null,
					level: p.level,
				},
			}),
		);
		const request = devModeOn()
			? practice.catch(async (err: unknown) => {
					// Not a practice puzzle: dev mode tries the bundled fixtures and dev puzzles.
					try {
						const puzzle = await loadDevPuzzle(id, ctrl.signal);
						return {
							status: 'ready',
							puzzle,
							daily: null,
							archive: false,
							session: { kind: 'puzzle', slot: slotFor({ kind: 'puzzle', puzzleId: puzzle.id }), puzzleId: puzzle.id, dateKey: null, number: null },
						} satisfies Ready;
					} catch {
						throw err;
					}
				})
			: practice;
		request.then(setLoad, (err: unknown) => {
			if (ctrl.signal.aborted) return;
			const error = toError(err);
			// No such practice puzzle: a bad link, not something still to come (UX-022).
			setLoad(error.kind === 'not-found' ? { status: 'missing' } : { status: 'error', error });
		});
		return () => ctrl.abort();
	}, [id, attempt]);

	useEffect(() => {
		if (load.status === 'ready') setPageTitle(load.puzzle.title);
	}, [load]);

	if (load.status === 'loading') return <GameLoading label="Loading the puzzle" back={back} dateKey={todayKey} />;
	if (load.status === 'missing' || load.status === 'later') return <Missing back={back} fromPlay />;
	if (load.status === 'error')
		return (
			<ErrorState
				error={load.error}
				what="this Sparrow"
				onRetry={() => setAttempt((n) => n + 1)}
				link={{ label: 'Back to Play', onClick: () => goUp(back) }}
				back={back}
				dev={dev ? { message: true } : undefined}
				game
				screen="game"
			/>
		);
	return (
		<Game
			key={load.session.slot}
			puzzle={load.puzzle}
			session={load.session}
			todayKey={todayKey}
			persistence={persistence}
			dev={dev}
			back={back}
			playNext={playNext}
		/>
	);
}

/** The board's usual size at each level, for the skeleton: the real board's sizing, so it lands where the board will. */
const SKELETON_SIDE: Record<Level, number> = { easy: 7, medium: 8, hard: 11 };

/**
 * A game on its way: the game's own frame from the first frame (the masthead with the way
 * back and ≡, the clue band and the keys' rows), so the board's slot is the size it will
 * be; once the wait is noticeable, the board's square, the day line and the keys in
 * skeleton (UX-015, QA4-4). Nothing moves when the puzzle arrives. Never the centred
 * lockup: that's for cold boot.
 */
function GameLoading({ label, back, level = DEFAULT_LEVEL, dateKey }: { label: string; back: BackTo; level?: Level; dateKey: DateKey }) {
	const side = SKELETON_SIDE[level];
	// The game's own tier (J3): wide is the one-row masthead band and the clue column.
	const layout = useGameLayout();
	const wide = layout === 'wide';
	// 10×10 and up: the game's one-line masthead (Header's `compact`).
	const compact = side >= 10 && !wide;
	// The day's lines hold their real height with a blank in the real type; the skeleton
	// bars sit in them once the wait is noticeable.
	const blank = (bar: string) => (
		<>
			{'\u00a0'}
			<Pending>
				<Skeleton className={bar} />
			</Pending>
		</>
	);
	const stage = (
		<main className="stage" aria-busy="true">
			<Pending label={label}>
				<Skeleton className="game-skel-board" style={{ '--cols': side, '--rows': side } as CSSProperties} />
			</Pending>
		</main>
	);
	const band = (
		<div className="cluebar-wrap">
			<div className="cluebar" />
		</div>
	);
	if (wide)
		return (
			<div className="app game-wait" data-weekday={weekdayKey(dateKey)} data-layout={layout}>
				<header className="hdr wide">
					<div className="hdr-top">
						<BackPill to={back} />
						<div className="hdr-title" aria-hidden="true">
							<span className="day game-skel-text">{blank('game-skel-day')}</span>
						</div>
						<div className="hdr-r">
							<AppMenu screen="game" />
						</div>
					</div>
				</header>
				<div className="game-body">
					<aside className="game-side" aria-hidden="true">
						<section className="controls">{band}</section>
					</aside>
					{stage}
				</div>
			</div>
		);
	return (
		<div className="app game-wait" data-weekday={weekdayKey(dateKey)} data-layout={layout}>
			<header className={compact ? 'hdr compact' : 'hdr'}>
				<div className="hdr-top">
					<BackPill to={back} />
					{compact && (
						<p className="day game-skel-text" aria-hidden="true">
							{blank('game-skel-day')}
						</p>
					)}
					<div className="hdr-r">
						<AppMenu screen="game" />
					</div>
				</div>
				{!compact && (
					<div className="hdr-day" aria-hidden="true">
						<span className="day game-skel-text">{blank('game-skel-day')}</span>
						<span className="meta game-skel-text">{blank('game-skel-meta')}</span>
					</div>
				)}
			</header>
			{stage}
			<section className="controls" aria-hidden="true">
				{band}
				<div className="kbd">
					{KEY_ROWS.map((row, r) => (
						<div className="krow" key={r}>
							{r === 1 && <span className="half" />}
							<Pending>
								{row.map((key) => (
									<Skeleton key={key} className={key === 'SWAP' || key === 'BACK' ? 'game-skel-key wide' : 'game-skel-key'} />
								))}
							</Pending>
							{r === 1 && <span className="half" />}
						</div>
					))}
				</div>
			</section>
		</div>
	);
}

/**
 * A daily that isn't out yet: the day it comes out, and today's instead (QA2-7). Tomorrow's,
 * opened from a link (a friend's day can start before ours), is out at midnight, in the words
 * Play's search uses (J10).
 */
function NotOut({ number, date, today, tomorrow, back }: { number: number; date: DateKey; today: number; tomorrow: boolean; back: BackTo }) {
	const line = tomorrow ? 'Not out here yet.' : `Sparrow No. ${number} is out on ${weekdayName(date)} ${dayMonth(date)}.`;
	const sub = tomorrow ? 'It comes out at midnight.' : `Today’s is No. ${today}.`;
	useEffect(() => setPageTitle(tomorrow ? 'Not out here yet' : `No. ${number} isn’t out yet`), [number, tomorrow]);
	return (
		<StatePage back={back} screen="game" testId="game-later">
			<StateBody line={line} sub={sub}>
				<div className="state-actions">
					<button type="button" className="btn lg primary fit" onClick={() => goUp('/')}>
						Play today’s Sparrow
						<Wedge direction="right" />
					</button>
				</div>
			</StateBody>
		</StatePage>
	);
}

/** A link to a Sparrow that doesn't exist: say so, and point to where they all are (UX-022). */
function Missing({ back, fromPlay }: { back: BackTo; fromPlay: boolean }) {
	const line = 'Can’t find that Sparrow.';
	useEffect(() => setPageTitle(line), []);
	return (
		<StatePage back={back} screen="game" testId="game-missing">
			<StateBody line={line} sub="Check the link. Play has every Sparrow so far.">
				<div className="state-actions">
					<button type="button" className="btn lg primary fit" onClick={() => (fromPlay ? goUp(back) : navigate('/play'))}>
						{fromPlay ? 'Back to Play' : 'Go to Play'}
					</button>
				</div>
			</StateBody>
		</StatePage>
	);
}
